import React, { useState, useEffect } from 'react';
import DisButton from './design-system/DisButton';
import DisCard from './design-system/DisCard';

// URL de l'app sur l'App Store
const APP_STORE_URL = 'https://apps.apple.com/app/dis-maman/id6742518829';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Détecter iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    
    // Détecter Android
    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);
    
    // Détecter si déjà en mode standalone (app installée)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                     window.navigator.standalone || 
                     document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    
    // Vérifier si l'utilisateur a fermé le prompt aujourd'hui
    const lastClosedDate = localStorage.getItem('pwa-install-prompt-closed-date');
    const today = new Date().toDateString();
    const hasClosedToday = lastClosedDate === today;
    
    // Vérifier si l'utilisateur a déjà eu une première réponse IA réussie
    const hasFirstSuccess = localStorage.getItem('dismaman_first_success') === 'true';
    
    // Détecter si on peut afficher le prompt PWA
    const canShowPWA = iOS || android || 
                       window.location.protocol === 'https:' || 
                       window.location.hostname === 'localhost' ||
                       process.env.NODE_ENV === 'development';

    // Fonction pour afficher le prompt si toutes les conditions sont remplies
    const tryShowPrompt = () => {
      const hasSuccess = localStorage.getItem('dismaman_first_success') === 'true';
      if (!standalone && !hasClosedToday && canShowPWA && hasSuccess) {
        setShowPrompt(true);
      }
    };
    
    // Si l'utilisateur a déjà eu un succès précédemment, afficher après un court délai
    let timer;
    if (hasFirstSuccess) {
      timer = setTimeout(tryShowPrompt, 2000);
    }
    
    // Écouter l'événement personnalisé déclenché après la première réponse IA réussie
    const handleFirstSuccess = () => {
      // Petit délai pour laisser l'utilisateur apprécier la réponse avant de montrer le prompt
      setTimeout(tryShowPrompt, 3000);
    };
    
    window.addEventListener('dismaman:first-success', handleFirstSuccess);
    
    // Écouter l'événement beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Ne montrer que si l'utilisateur a déjà eu une expérience réussie
      const hasSuccess = localStorage.getItem('dismaman_first_success') === 'true';
      if (!hasClosedToday && !standalone && hasSuccess) {
        setShowPrompt(true);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('dismaman:first-success', handleFirstSuccess);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // iOS - Rediriger vers l'App Store
      window.open(APP_STORE_URL, '_blank');
      setShowPrompt(false);
      // Stocker la date de fermeture pour ne pas réafficher aujourd'hui
      localStorage.setItem('pwa-install-prompt-closed-date', new Date().toDateString());
    } else if (deferredPrompt) {
      // Android/Chrome - Installer la PWA
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
      if (outcome === 'accepted') {
        // Si accepté, ne plus montrer du tout
        localStorage.setItem('pwa-install-prompt-closed-date', 'installed');
      } else {
        // Si refusé, réafficher demain
        localStorage.setItem('pwa-install-prompt-closed-date', new Date().toDateString());
      }
    }
  };

  const handleClosePrompt = () => {
    setShowPrompt(false);
    // Stocker la date de fermeture pour réafficher demain
    localStorage.setItem('pwa-install-prompt-closed-date', new Date().toDateString());
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-slide-up">
      <DisCard className="bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow-2xl border-0">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 text-2xl">
            📱
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg mb-1">
              Installez Dis Maman ! 🚀
            </h3>
            
            {isIOS ? (
              <div className="text-sm text-white/90 mb-3">
                <p className="mb-2">
                  Téléchargez l'app native sur l'App Store pour une meilleure expérience ! 🍎
                </p>
                <div className="bg-white/20 rounded-lg p-2 text-xs">
                  <p>✓ Notifications push</p>
                  <p>✓ Face ID / Touch ID</p>
                  <p>✓ Performance optimisée</p>
                </div>
              </div>
            ) : isAndroid ? (
              <p className="text-sm text-white/90 mb-3">
                Installez l'app sur votre écran d'accueil pour un accès rapide ! 📱✨
              </p>
            ) : (
              <p className="text-sm text-white/90 mb-3">
                Installez l'app pour un accès rapide et une meilleure expérience ! 🌟
              </p>
            )}
          </div>
          
          <div className="flex-shrink-0">
            <button
              onClick={handleClosePrompt}
              className="text-white/70 hover:text-white text-xl font-bold w-6 h-6 flex items-center justify-center"
            >
              ×
            </button>
          </div>
        </div>
        
        <div className="flex space-x-2 mt-3">
          {isIOS && (
            <DisButton
              onClick={handleInstallClick}
              size="sm"
              className="flex-1 bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
            >
              🍎 App Store
            </DisButton>
          )}
          
          {(isAndroid || deferredPrompt) && !isIOS && (
            <DisButton
              onClick={handleInstallClick}
              size="sm"
              className="flex-1 bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
            >
              📲 Installer
            </DisButton>
          )}
          
          <DisButton
            onClick={handleClosePrompt}
            size="sm"
            variant="secondary"
            className="flex-1 bg-white/10 hover:bg-white/20 text-white border border-white/30"
          >
            Plus tard
          </DisButton>
        </div>
      </DisCard>
    </div>
  );
};

export default PWAInstallPrompt;